import QueryString from 'smg/querystring';
import * as template from 'smg/template';

type cardTags = Record<string, string[]>;

main();

function main() {
    initBanner();
    initFilters();
}

function initBanner() {
    const $banner = document.querySelector('#banner-image-parent') as HTMLElement;
    const $header = document.querySelector('header') as HTMLElement;
    $header.after($banner);
}

function productCount(n: number) {
    return n === 1 ? '1 product' : `${n} products`;
}

function initFilters() {
    const $chips = [...document.querySelectorAll<HTMLButtonElement>('[data-filter-key]')];
    const $cards = [...document.querySelectorAll<HTMLElement>('#product-cards > [data-filters]')];
    const $grid = document.querySelector('#product-cards') as HTMLElement;
    const $noMatches = document.querySelector('#stock-no-matches') as HTMLElement;
    const $counts = document.querySelectorAll<HTMLElement>('[data-filter-count]');
    const $clearPanel = document.querySelector('#stock-filters-clear-panel') as HTMLButtonElement;
    const $clearBar = document.querySelector('#stock-filters-clear-bar') as HTMLButtonElement;
    const $clearStack = document.querySelector('#stock-filters-clear-stack') as HTMLButtonElement;
    const $clearInline = document.querySelector('#stock-filters-clear-inline') as HTMLElement;
    const $chosenRow = document.querySelector('#stock-filters-chosen') as HTMLElement;
    const $clearSheet = document.querySelector('#stock-filters-clear-sheet') as HTMLButtonElement;
    const $open = document.querySelector('#stock-filters-open') as HTMLButtonElement;
    const $show = document.querySelector('[data-filter-show]') as HTMLButtonElement;

    const tags = new Map($cards.map($card => [$card, JSON.parse($card.dataset.filters ?? '{}') as cardTags]));

    // The panel and the sheet render the same chips; one entry per key/value, in page order.
    const values = new Map<string, Map<string, string>>();
    for (const $chip of $chips) {
        const key = $chip.dataset.filterKey ?? '';
        if (!values.has(key)) {
            values.set(key, new Map());
        }
        values.get(key)?.set($chip.dataset.filterValue ?? '', $chip.dataset.filterLabel ?? '');
    }

    const selection = new Map<string, Set<string>>();
    for (const [key, known] of values) {
        const fromURL = (QueryString().get(key) ?? '').split(',').filter(v => known.has(v));
        selection.set(key, new Set(fromURL));
    }

    // OR within a grouping, AND across groupings; skipKey ignores one grouping so a chip's count says what adding it would show.
    const matches = ($card: HTMLElement, skipKey = '') => {
        const ownTags = tags.get($card) ?? {};
        return [...selection].every(([key, chosen]) =>
            key === skipKey || chosen.size === 0 || (ownTags[key] ?? []).some(v => chosen.has(v)));
    };

    const toggle = (key: string, value: string, on: boolean) => {
        const chosen = selection.get(key);
        if (on) {
            chosen?.add(value);
        } else {
            chosen?.delete(value);
        }
        render();
    };

    const clear = () => {
        for (const chosen of selection.values()) {
            chosen.clear();
        }
        render();
    };

    const render = () => {
        const visible = $cards.filter($card => matches($card));
        for (const $card of $cards) {
            $card.classList.toggle('d-none', !visible.includes($card));
        }
        $grid.classList.toggle('d-none', visible.length === 0);
        $noMatches.classList.toggle('d-none', visible.length > 0);

        const count = productCount(visible.length);
        $counts.forEach($count => $count.textContent = count);
        if (visible.length === 0) {
            $show.textContent = 'No Products';
        } else if (visible.length === 1) {
            $show.textContent = 'Show 1 Product';
        } else {
            $show.textContent = `Show ${visible.length} Products`;
        }

        for (const $chip of $chips) {
            const key = $chip.dataset.filterKey ?? '';
            const value = $chip.dataset.filterValue ?? '';
            const label = $chip.dataset.filterLabel ?? '';
            const chosen = selection.get(key)?.has(value) ?? false;
            const n = $cards.filter($card => matches($card, key) && (tags.get($card)?.[key] ?? []).includes(value)).length;
            $chip.textContent = `${label} (${n})`;
            $chip.setAttribute('aria-label', `${label}, ${productCount(n)}`);
            $chip.setAttribute('aria-pressed', String(chosen));
            $chip.classList.toggle('active', chosen);
            $chip.disabled = !chosen && n === 0;
        }

        const active = [...selection.values()].some(chosen => chosen.size > 0);
        $clearPanel.classList.toggle('invisible', !active);
        $clearBar.classList.toggle('d-none', !active);
        $clearStack.classList.toggle('d-none', !active);
        $chosenRow.classList.toggle('d-none', !active);
        $clearSheet.disabled = !active;

        document.querySelectorAll('[data-filter-chosen]').forEach($chosen => $chosen.remove());
        for (const [key, chosen] of selection) {
            for (const [value, label] of values.get(key) ?? []) {
                if (!chosen.has(value)) {
                    continue;
                }
                const $chosen = template.Clone('#stock-filter-chosen-template').firstElementChild as HTMLButtonElement;
                $chosen.dataset.filterChosen = '';
                ($chosen.querySelector('span') as HTMLElement).textContent = label;
                $chosen.setAttribute('aria-label', `Remove ${label} filter`);
                $chosen.addEventListener('click', () => {
                    toggle(key, value, false);
                    $open.focus();
                });
                $clearInline.before($chosen);
            }
        }

        for (const [key, chosen] of selection) {
            if (chosen.size > 0) {
                QueryString().set(key, [...chosen].join(','));
            } else {
                QueryString().delete(key);
            }
        }
    };

    for (const $chip of $chips) {
        $chip.addEventListener('click', () => {
            const key = $chip.dataset.filterKey ?? '';
            const value = $chip.dataset.filterValue ?? '';
            toggle(key, value, !(selection.get(key)?.has(value) ?? false));
        });
    }
    document.querySelectorAll('[data-filter-clear]').forEach($clear => $clear.addEventListener('click', clear));

    render();
}
